import {
  Button,
  CodeSnippet,
  InlineNotification,
  PasswordInput,
  SkeletonText,
  Stack,
  TextInput,
  Toggle,
} from '@carbon/react';
import { asProblem, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import './signIn.scss';

type Settings = HoComponents['schemas']['SsoSettingsDto'];
const settingsKey = ['ho', 'sso-settings'] as const;
const list = (text: string) =>
  text
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * Sign-in with the company's identity provider (ADR 0121): OpenID Connect, its address, client id and secret (never shown again),
 * the values that mean it asked for a second factor, and the email domains a first sign-in may match. Turned on, staff sign in with
 * it; passwords stay only for break-glass accounts. Company-wide admins change it; every change is audited.
 */
export function SsoSettingsPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.org.sign_in.manage');
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/sso-settings');
      if (!data) throw error;
      return data;
    },
  });
  const [edits, setEdits] = useState<
    Partial<Settings> & { clientSecret?: string; mfaText?: string; domainsText?: string }
  >({});
  const saved = current.data;
  const value = { ...saved, ...edits } as Settings & typeof edits;
  const input = {
    enabled: value.enabled,
    displayName: value.displayName,
    authority: value.authority,
    clientId: value.clientId,
    clientSecret: edits.clientSecret || null,
    scopes: value.scopes,
    mfaValues: edits.mfaText !== undefined ? list(edits.mfaText) : [...(saved?.mfaValues ?? [])],
    emailDomains: edits.domainsText !== undefined ? list(edits.domainsText) : [...(saved?.emailDomains ?? [])],
  };

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/identity/sso-settings', {
        body: input,
        headers: { 'If-Match': `"${saved?.version ?? 0}"` },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setEdits({});
      queryClient.setQueryData(settingsKey, next);
    },
  });
  const test = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/identity/sso-settings/test', { body: input });
      if (!data) throw error;
      return data;
    },
  });

  if (current.isLoading) return <SkeletonText paragraph lineCount={4} aria-label={t('common.loading')} />;
  if (!saved)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(current.error))}
      />
    );

  const edit = (next: typeof edits) => {
    save.reset();
    test.reset();
    setEdits((all) => ({ ...all, ...next }));
  };
  return (
    <section className="pos-admin-page">
      <PageHeader title={t('signIn.sso.title')} description={t('signIn.sso.hint')} />
      <Stack gap={6}>
        <Toggle
          id="sso-enabled"
          labelText={t('signIn.sso.enabled')}
          labelA={t('admin.no')}
          labelB={t('admin.yes')}
          toggled={value.enabled}
          disabled={!canManage}
          onToggle={(on) => edit({ enabled: on })}
        />
        <TextInput
          id="sso-name"
          labelText={t('signIn.sso.displayName')}
          helperText={t('signIn.sso.displayNameHint')}
          value={value.displayName}
          readOnly={!canManage}
          onChange={(e) => edit({ displayName: e.target.value })}
        />
        <TextInput
          id="sso-authority"
          labelText={t('signIn.sso.authority')}
          helperText={t('signIn.sso.authorityHint')}
          value={value.authority}
          readOnly={!canManage}
          onChange={(e) => edit({ authority: e.target.value })}
        />
        <TextInput
          id="sso-client"
          labelText={t('signIn.sso.clientId')}
          value={value.clientId}
          readOnly={!canManage}
          onChange={(e) => edit({ clientId: e.target.value })}
        />
        <PasswordInput
          id="sso-secret"
          labelText={t('signIn.sso.clientSecret')}
          helperText={saved.hasClientSecret ? t('signIn.sso.secretSet') : t('signIn.sso.secretNotSet')}
          showPasswordLabel={t('carbon.showPassword')}
          hidePasswordLabel={t('carbon.hidePassword')}
          value={edits.clientSecret ?? ''}
          autoComplete="new-password"
          readOnly={!canManage}
          onChange={(e) => edit({ clientSecret: e.target.value })}
        />
        <TextInput
          id="sso-mfa"
          labelText={t('signIn.sso.mfaValues')}
          helperText={t('signIn.sso.mfaValuesHint')}
          value={edits.mfaText ?? saved.mfaValues.join(', ')}
          readOnly={!canManage}
          onChange={(e) => edit({ mfaText: e.target.value })}
        />
        <TextInput
          id="sso-domains"
          labelText={t('signIn.sso.emailDomains')}
          helperText={t('signIn.sso.emailDomainsHint')}
          value={edits.domainsText ?? saved.emailDomains.join(', ')}
          readOnly={!canManage}
          onChange={(e) => edit({ domainsText: e.target.value })}
        />
        <Stack gap={3}>
          <p className="pos-sign-in__label">{t('signIn.sso.callback')}</p>
          <CodeSnippet type="single" feedback={t('signIn.copied')} aria-label={t('signIn.sso.callback')}>
            {saved.callbackUrl}
          </CodeSnippet>
        </Stack>
        {canManage && (
          <div className="pos-sign-in__actions">
            <Button disabled={Object.keys(edits).length === 0 || save.isPending} onClick={() => save.mutate()}>
              {t('admin.save')}
            </Button>
            <Button kind="tertiary" disabled={!value.authority || test.isPending} onClick={() => test.mutate()}>
              {t('signIn.sso.test')}
            </Button>
          </div>
        )}
        {(save.error ?? test.error) && (
          <InlineNotification
            kind="error"
            role="alert"
            lowContrast
            hideCloseButton
            title={problemMessage(t, asProblem(save.error ?? test.error))}
          />
        )}
        {test.data && (
          <InlineNotification
            kind={test.data.ok ? 'success' : 'error'}
            role="status"
            lowContrast
            hideCloseButton
            title={
              test.data.ok ? t('signIn.sso.testOk', { issuer: test.data.issuer ?? '' }) : t('signIn.sso.testFailed')
            }
          />
        )}
        {save.isSuccess && (
          <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={t('signIn.sso.saved')} />
        )}
      </Stack>
    </section>
  );
}
